/**
 * Production adapter for `smooth_structure`: the SAME calls the toolbar's
 * Smoothing panel makes (`SmoothingPanel` via `useRoiSdfOperation`) —
 * `applyGaussianSmoothingGL(texture, level)` for Gaussian Smoothing and
 * `SDFOperations.applyMorphologicalClosing(texture, level)` for Surface
 * Smoothing — then `roi.replaceTexture(result, label)`, a contour-layer
 * update and the panel's success snackbar (same message IDs), shared in
 * {@link makeInPlaceEngineDeps}. Browser glue covered by the manual demo;
 * the logic is unit-tested through fakes.
 */
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { applyGaussianSmoothingGL } from '~/engine/util/algorithms/sdf/GaussianSmoothingGL';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { environmentState } from '~/state/atoms/engineAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { projectStructureFacts } from '../core/engineStructureFacts';
import {
    makeInPlaceEngineDeps,
    requireSourceTexture,
    toComputedGeometry,
} from '../core';
import type { SmoothDeps, SmoothingMethod } from './toolRunner';

/** The panel's undo labels and success message IDs, per method. */
const PANEL: Record<SmoothingMethod, { label: string; successId: string }> = {
    gaussian: {
        label: 'Gaussian Smoothing',
        successId: 'postProcessing.smoothing.success',
    },
    surface: {
        label: 'Surface Smoothing',
        successId: 'postProcessing.smoothing.preserveVolume.success',
    },
};

export function useSmoothStructureDeps(): () => Omit<
    SmoothDeps,
    'pendingMutationExists'
> {
    const env = useRecoilValue(environmentState);
    const structureSet = useRecoilValue(currentStructureSetState);
    const intl = useIntl();

    return useCallback(() => {
        let lastLevel = 0;
        return {
            structures: projectStructureFacts(structureSet, env),
            inPlace: (method: SmoothingMethod) =>
                makeInPlaceEngineDeps(
                    env,
                    structureSet,
                    PANEL[method].label,
                    (name) =>
                        intl.formatMessage(
                            { id: PANEL[method].successId },
                            { name, level: lastLevel }
                        )
                ),
            smooth: async (roiNumber, method, level) => {
                lastLevel = level;
                const src = requireSourceTexture(env, roiNumber);
                const result =
                    method === 'gaussian'
                        ? await applyGaussianSmoothingGL(src, level)
                        : await SDFOperations.applyMorphologicalClosing(
                              src,
                              level
                          );
                // undefined = another contour operation holds the GL guard,
                // or the source had no readable SDF data.
                if (!result) {
                    throw new Error(
                        'smoothing could not run (another contour operation ' +
                            'may be in progress); try again'
                    );
                }
                return toComputedGeometry(result);
            },
        };
    }, [env, structureSet, intl]);
}
